import { z } from 'zod'

export const LessonDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  duration: z.string().nullable(),
  level: z.string(),
  tagName: z.string(),
  type: z.enum(['video', 'text', 'quiz', 'assignment', 'external']).optional(),
  externalUrl: z.string().nullable().optional(),
})

export type LessonData = z.infer<typeof LessonDataSchema>

export const CourseLearnDataSchema = z.object({
  course: z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().nullable().optional(),
    category: z.string().nullable().optional(),
    level: z.string().nullable().optional(),
    duration: z.string().nullable().optional(),
    instructorId: z.string().nullable().optional(),
    imageUrl: z.string().nullable().optional(),
    isPublished: z.boolean().optional(),
    markdownSummary: z.string().nullable().optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
    lessons: z.array(LessonDataSchema),
  }),
  enrollment: z.object({
    id: z.string(),
    userId: z.string(),
    courseId: z.string(),
    enrolledAt: z.date(),
    status: z.string().optional(),
    completedAt: z.date().nullable().optional(),
  }),
  progress: z.object({
    percentage: z.number().min(0).max(100),
    completed: z.number().min(0),
    total: z.number().min(0),
  }),
  completedLessonIds: z.array(z.string()),
})

export type CourseLearnData = z.infer<typeof CourseLearnDataSchema>

export const LessonSelectionResultSchema = z.object({
  currentLesson: LessonDataSchema.nullable(),
  currentLessonIndex: z.number(),
  prevLesson: LessonDataSchema.nullable(),
  nextLesson: LessonDataSchema.nullable(),
})

export type LessonSelectionResult = z.infer<typeof LessonSelectionResultSchema>

export const RawLessonDataSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  videoUrl: z.string().nullable(),
  videoTotalDurationSeconds: z.number().nullable(),
  level: z.string(),
  tagName: z.string(),
  type: z.string(),
  externalUrl: z.string().nullable(),
})

export type RawLessonData = z.infer<typeof RawLessonDataSchema>

export function transformRawLessonToLessonData(rawLesson: RawLessonData): LessonData {
  const lessonType = rawLesson.type as
    | 'video'
    | 'text'
    | 'quiz'
    | 'assignment'
    | 'external'
    | undefined

  return {
    id: rawLesson.id,
    title: rawLesson.title,
    description: rawLesson.description,
    duration: rawLesson.videoTotalDurationSeconds
      ? `${Math.floor(rawLesson.videoTotalDurationSeconds / 60)}:${String(rawLesson.videoTotalDurationSeconds % 60).padStart(2, '0')}`
      : null,
    level: rawLesson.level,
    tagName: rawLesson.tagName,
    type: lessonType,
    externalUrl: rawLesson.externalUrl,
  }
}

const LEVEL_ORDER: Record<string, number> = {
  ENTRY: 1,
  BASIC: 2,
  ADVANCED: 3,
  EXPERT: 4,
}

export function sortLessons(lessons: LessonData[]): LessonData[] {
  return [...lessons].sort((a, b) => {
    const levelA = LEVEL_ORDER[a.level] ?? 999
    const levelB = LEVEL_ORDER[b.level] ?? 999
    if (levelA !== levelB) {
      return levelA - levelB
    }
    return a.tagName.localeCompare(b.tagName, 'ja')
  })
}
